/**
 * Shared coach-playback tracker for barge-in (design §11).
 *
 * Tracks the currently-playing coach clip so that when the user starts to speak
 * (or taps) the composer can record an approximate interruption and stop
 * playback. Platform-agnostic: it imports the platform `coach-audio` (web plays;
 * native is a no-op until expo-audio is wired), so on native nothing is playing
 * and barge-in is simply inert.
 */
import { stopCoachAudio } from "./coach-audio";

export interface InterruptionMeta {
  trigger: "voice" | "tap";
  elapsedMs: number;
  totalDurationMs?: number;
}

interface ActivePlayback {
  messageId: string;
  startedAt: number;
  durationMs?: number;
}

let active: ActivePlayback | null = null;
let pending: InterruptionMeta | null = null;

type PlaybackListener = (playing: boolean) => void;
const listeners = new Set<PlaybackListener>();

function notify(): void {
  const playing = active !== null;
  listeners.forEach((l) => l(playing));
}

/** Subscribe to coach playback start/stop (for voice-activated barge-in). */
export function subscribePlayback(listener: PlaybackListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function markPlaybackStart(messageId: string): void {
  active = { messageId, startedAt: Date.now() };
  notify();
}

export function setPlaybackDuration(messageId: string, durationMs: number): void {
  if (active?.messageId === messageId) active.durationMs = durationMs;
}

export function markPlaybackEnd(messageId: string): void {
  if (active?.messageId === messageId) {
    active = null;
    notify();
  }
}

export function isCoachPlaying(): boolean {
  return active !== null;
}

/**
 * Record a barge-in against the active clip and stop playback. Returns true if a
 * clip was actually interrupted. No-op (false) when nothing is playing.
 */
export function bargeIn(trigger: "voice" | "tap"): boolean {
  if (!active) return false;
  pending = {
    trigger,
    elapsedMs: Date.now() - active.startedAt,
    totalDurationMs: active.durationMs,
  };
  active = null;
  notify();
  stopCoachAudio();
  return true;
}

/** Take (and clear) the pending interruption to attach to the next turn. */
export function takePendingInterruption(): InterruptionMeta | null {
  const p = pending;
  pending = null;
  return p;
}
